import React from 'react';

import {FloatingTree, useFloatingParentNodeId} from '@floating-ui/react';

import {FlatButton} from '@pb/kit-component-button';
import {Icon, Icons} from '@pb/kit-component-icon';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {DropdownBase} from './dropdown-base';
import styles from './dropdown.module.styl';
import {useDropdown} from './use-dropdown';

import type {DropdownBaseProps} from './dropdown-base';
import type {UseDropdownProps, UseDropdownReturnType} from './use-dropdown';
import type {Placement, UseFloatingOptions} from '@floating-ui/react';
import type {FlatButtonProps} from '@pb/kit-component-button';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';

const classNames = withCssModule(styles);

type NativeProps = Omit<ComponentPropsWithoutRef<'div'>, 'role' | 'onToggle'>;

export interface DropdownProps extends NativeProps {

    /**
     * Render prop for the trigger element. Receives `anchorRef`, `getAnchorProps`,
     * `positionRef`, and `defaultAnchorProps` — merge them onto your trigger node.
     * When omitted, a default `<FlatButton>` with a chevron-down icon is used.
     */
    anchor?: (props: {
        anchorRef: UseDropdownReturnType['anchorRef'],
        /** Merges Floating UI interaction props onto an arbitrary set of element props. */
        getAnchorProps: UseDropdownReturnType['getAnchorProps'],
        positionRef: UseDropdownReturnType['positionRef'],
        defaultAnchorProps?: FlatButtonProps,
    }) => ReactNode,

    /** Content rendered inside the floating panel. */
    children: ReactNode,

    /**
     * Component used to wrap `children` inside the floating panel. Defaults to
     * a plain `<div>` with dropdown-specific styling. Override to add scrolling,
     * custom padding, or a `<List role="menu">` wrapper.
     */
    bodyComponent?: DropdownBaseProps['bodyComponent'],

    /**
     * Props forwarded to `FloatingPortal`. Use to control the portal mount target
     * or add a class to the portal wrapper `<div>`.
     */
    portalProps?: DropdownBaseProps['portalProps'],

    /**
     * Props forwarded to Floating UI's `FloatingFocusManager` (e.g. `initialFocus`,
     * `returnFocus`, `modal`). Controls focus trapping behavior inside the panel.
     */
    floatingFocusManagerProps?: DropdownBaseProps['floatingFocusManagerProps'],

    /**
     * shouldUnmount=false will keep the dropdown content in the DOM even when it's not shown
     *
     * @deprecated It will be removed in 6 months 09-15-2025. render your modals outside of the dropdown, see example in the Storybook
     * */
    shouldUnmount?: boolean,


    /**
     * Positioning strategy
     * */
    type?: 'ABSOLUTE' | 'FIXED',

    /**
     * External 'shown' state
     * */
    shown?: boolean,

    /**
     * Setter for the external 'shown' state
     * */
    setShown?: UseDropdownProps['setShown'],

    /**
     * If show the floating on over or on click
     * @deprecated It will be removed in 6 months 03-15-2026. Use `hoverEnabled` or `clickEnabled` instead.
     * */
    hoverable?: boolean,

    /**
     * Max possible height of the Dropdown, in px
     */
    maxHeight?: number,

    /**
     * Max possible width of the Dropdown, in px
     */
    maxWidth?: number,

    /**
     * Correction of the floating element vertical position, in pixels
     */
    verticalOffset?: number,
    /**
     * Correction of the floating element horizontal position, in pixels
     */
    horizontalOffset?: number,

    /**
     * Callback on toggle shown state
     * */
    onToggle?: (shown: boolean) => unknown,

    /**
     * Hide when mouse leave the floating, true by default, true for hoverable Dropdowns
     * */
    hideOnBodyLeave?: boolean,

    /**
     * ARIA role
     * */
    role?: 'tooltip' | 'dialog' | 'alertdialog' | 'menu' | 'listbox' | 'grid' | 'tree' | 'select' | 'label' | 'combobox',

    /**
     * If Floating list navigation is enabled. Use for Dropdowns with a list of options
     * */
    listNavigationEnabled?: boolean,

    /** Ordered list of Floating UI placements the panel may occupy. The first
     *  placement that fits the viewport is selected automatically. */
    allowedPlacements?: Placement[],

    /** Additional Floating UI middleware (e.g. `offset`, `shift`, `hide`) appended
     *  to the default middleware stack. */
    middlewares?: UseFloatingOptions['middleware'],

    /**
     * If the arrow should be shown
     * */
    arrowEnabled?: boolean,

    /**
     * Props for Floating UI useHover https://floating-ui.com/docs/useHover
     * */
    hoverProps?: UseDropdownProps['hoverProps'],

    /**
     * Props for the Arrow component of the Dropdown element
     */
    arrowProps?: UseDropdownProps['arrowProps'],

    /**
     * Default anchor props.
     * */
    defaultAnchorProps?: FlatButtonProps,

    /**
     * Placement of the Dropdown
     * */
    placement?: UseDropdownProps['placement'],

    /**
     * If true, the dropdown will have the same width as the anchor element
     */
    equalWidth?: UseDropdownProps['equalWidth'],

    /**
     * If true, the dropdown will open on click
     */
    clickEnabled?: UseDropdownProps['clickEnabled'],

    /**
     * If true, the dropdown will open on hover
     */
    hoverEnabled?: UseDropdownProps['hoverEnabled'],

    /**
     * Props for Floating UI useDismiss https://floating-ui.com/docs/useDismiss
     */
    dismissProps?: UseDropdownProps['dismissProps'],

    /**
     * Props for Floating UI useClick https://floating-ui.com/docs/useClick
     */
    clickProps?: UseDropdownProps['clickProps'],

    /**
     * Props for useUpdate from useFloating hook
     */
    updateProps?: UseDropdownProps['updateProps'],
}

const getDefaultAnchor: DropdownProps['anchor'] = ({anchorRef, getAnchorProps, positionRef, defaultAnchorProps}) => (
    <FlatButton
        className={classNames('default-anchor')}
        variant="secondary"
        endIcon={<Icon size="Size_14" type={Icons.ANGLE_DOWN} ref={positionRef}/>}
        {...getAnchorProps(defaultAnchorProps)}
        ref={anchorRef}
    />
);

const DropdownInner = ({
    defaultAnchorProps,
    anchor = getDefaultAnchor,
    children,
    bodyComponent,
    portalProps,
    floatingFocusManagerProps,
    shouldUnmount,
    maxHeight,
    maxWidth,
    verticalOffset,
    horizontalOffset,
    role,
    allowedPlacements,
    type,
    shown,
    setShown,
    onToggle,
    hideOnBodyLeave,
    hoverable,
    middlewares,
    listNavigationEnabled,
    arrowEnabled,
    arrowProps,
    hoverProps,
    placement,
    equalWidth,
    clickEnabled,
    hoverEnabled,
    dismissProps,
    clickProps,
    updateProps,
    ...nativeProps
}: DropdownProps) => {

    const {
        dropdownRef,
        anchorRef,
        getAnchorProps,
        getDropdownProps,
        positionRef,
    } = useDropdown({
        maxHeight,
        maxWidth,
        verticalOffset,
        horizontalOffset,
        role,
        allowedPlacements,
        type,
        shown,
        setShown,
        onToggle,
        hideOnBodyLeave,
        hoverable,
        middlewares,
        listNavigationEnabled,
        arrowEnabled,
        arrowProps,
        hoverProps,
        placement,
        equalWidth,
        clickEnabled,
        hoverEnabled,
        dismissProps,
        clickProps,
        updateProps,
    });

    return (
        <>
            {anchor({
                anchorRef,
                getAnchorProps,
                positionRef,
                defaultAnchorProps,
            })}
            <DropdownBase
                bodyComponent={bodyComponent}
                portalProps={portalProps}
                floatingFocusManagerProps={{
                    ...(hoverable ? {returnFocus: false} : {}), // without this it returns focus to the last clicked element and browser scrolls to it
                    ...floatingFocusManagerProps,
                }}
                shouldUnmount={shouldUnmount}
                {...getDropdownProps(nativeProps)}
                ref={dropdownRef}>
                {children}
            </DropdownBase>
        </>
    );
};

/**
 * General-purpose floating panel anchored to a trigger element, built on
 * Floating UI.
 *
 * Renders any content (`children`) inside a positioned overlay that opens on
 * click and/or hover. The trigger is supplied via the `anchor` render prop,
 * which receives `anchorRef`, `getAnchorProps`, and `positionRef` — merge
 * these onto your trigger element. A default button trigger is used when
 * `anchor` is omitted.
 *
 * Supports controlled (`shown` + `setShown`) or uncontrolled open state.
 * Customize positioning with `placement`, `allowedPlacements`, `verticalOffset`,
 * `horizontalOffset`, and `middlewares`. Use `role` to set the correct ARIA
 * role for the overlay (`"menu"`, `"listbox"`, `"dialog"`, etc.).
 *
 * Nested dropdowns are handled automatically via `FloatingTree` (the
 * component wraps itself in `FloatingTree` when it is the root dropdown).
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-dropdown--documentation}
 */
export const Dropdown = (props: DropdownProps) => {
    const parentId = useFloatingParentNodeId();

    if (parentId === null) {
        return (
            <FloatingTree>
                <DropdownInner {...props}/>
            </FloatingTree>
        );
    }

    return <DropdownInner {...props}/>;
};

Dropdown.displayName = 'Dropdown';
